import { areHeraldProductsEligibleToCreateSubmission, getIsHeraldSubmission } from '@common/utils';
import { Chip, Stack } from '@common-components';
import { WarningAmber } from '@icons';
import { SubmissionMarketRequestStatus, SubmissionMethod } from 'enums';
import { useRetrieveHeraldApplication } from 'hooks';
import { messages } from 'i18n';
import { ExtendedMarketRequest } from 'types';
import { RouteKey } from 'broker/enums';
import { useNavigate } from 'broker/hooks';
import { SubmissionMethodChip } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/AddProducts/Components/SubmissionMethodChip';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useSubmissionMarketRequestActions } from './useSubmissionMarketRequestActions';

interface AvailableSubmitChipsProps {
  marketRequest: ExtendedMarketRequest;
  setIsSubmittingMarketRequest?: (isSubmitting: boolean) => void;
}
export default function AvailableSubmitChips({
  marketRequest,
  setIsSubmittingMarketRequest,
}: AvailableSubmitChipsProps) {
  const { submission } = useSubmissionsWorkspace();
  const { editSubmissionUrl } = useWorkspaceUrls();
  const navigate = useNavigate();

  const isDynamicSubmission = !!(submission && getIsHeraldSubmission(submission));

  const { submitToMarketByPortal, submitProductViaApi } = useSubmissionMarketRequestActions(
    marketRequest,
    setIsSubmittingMarketRequest,
  );

  const { data: heraldApplicationResponse } = useRetrieveHeraldApplication({
    id: submission?.heraldData?.applicationId!,
    enabled: isDynamicSubmission,
  });

  const quoteExit =
    marketRequest.insuranceProduct?.externalProductId &&
    heraldApplicationResponse?.quote_exits?.find(
      (quote) => quote.product_id === marketRequest.insuranceProduct?.externalProductId,
    );

  const showSubmitViaPortal =
    marketRequest.insuranceProduct?.submissionPortal &&
    marketRequest.status === SubmissionMarketRequestStatus.ReadyToMarket;

  const showNotCompletedApiApplication =
    marketRequest.insuranceProduct?.externalProductId &&
    submission &&
    !areHeraldProductsEligibleToCreateSubmission(
      [marketRequest.insuranceProduct.externalProductId],
      submission.heraldData,
    ).eligible &&
    isDynamicSubmission;

  const showSubmitViaApi =
    submission &&
    marketRequest.status === SubmissionMarketRequestStatus.ReadyToMarket &&
    marketRequest.insuranceProduct?.externalProductId &&
    marketRequest.submissionMethod !== SubmissionMethod.API &&
    isDynamicSubmission;

  if (!showSubmitViaPortal && !showSubmitViaApi) {
    return null;
  }

  const isSubmitViaApiDisabled = !!showNotCompletedApiApplication || !!quoteExit;

  return (
    <Stack gap={0.5} direction="row" flexWrap="wrap">
      {showSubmitViaPortal && (
        <SubmissionMethodChip
          method={SubmissionMethod.Portal}
          label={messages.buttons.submitViaPortal}
          onClick={submitToMarketByPortal}
        />
      )}
      {showSubmitViaApi && (
        <SubmissionMethodChip
          method={SubmissionMethod.API}
          label={messages.buttons.submitViaApi}
          disabled={isSubmitViaApiDisabled}
          disabledTooltipContent={
            quoteExit
              ? quoteExit.status_details[0].status_text.applicant_facing_text
              : marketRequest.insuranceProduct?.externalProductId &&
                areHeraldProductsEligibleToCreateSubmission(
                  [marketRequest.insuranceProduct.externalProductId],
                  submission?.heraldData,
                ).reason
          }
          onClick={submitProductViaApi}
        />
      )}
      {showNotCompletedApiApplication && showSubmitViaApi && (
        <Chip
          label={messages.general.submissionInformationIncomplete}
          color="error.main"
          textColor="error.main"
          iconColor="error.main"
          variant="outlined"
          size="small"
          startIcon={WarningAmber}
          onClick={() => {
            navigate(editSubmissionUrl, {
              routeKey: RouteKey.SubmissionEdit,
              state: undefined,
            });
          }}
        />
      )}
    </Stack>
  );
}
